import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { X, Tag, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TagInput — Reusable tag autocomplete component.
 *
 * Props:
 *   value: string[] — currently selected tags
 *   onChange: (tags: string[]) => void — callback when tags change
 *   placeholder: string — input placeholder text
 *   singleSelect: boolean — if true, only one tag can be selected (for step configs)
 *   singleValue: string — the single selected tag (use with singleSelect mode)
 *   onSingleChange: (tag: string) => void — callback for single select mode
 *   className: string — additional classes for the wrapper
 */
export default function TagInput({
    value = [],
    onChange,
    placeholder = 'Add tags...',
    singleSelect = false,
    singleValue = '',
    onSingleChange,
    className = '',
}) {
    const { getAuthHeader } = useAuth();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    // Fetch suggestions with debounce
    const fetchSuggestions = useCallback(async (search) => {
        setLoading(true);
        try {
            const params = search ? `?search=${encodeURIComponent(search)}` : '';
            const res = await axios.get(`${API}/tags${params}`, { headers: getAuthHeader() });
            setSuggestions(res.data || []);
        } catch {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        if (isOpen) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => fetchSuggestions(query), 300);
        }
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [query, isOpen, fetchSuggestions]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentTags = singleSelect ? (singleValue ? [singleValue] : []) : value;

    const addTag = (tag) => {
        const trimmed = tag.trim();
        if (!trimmed) return;

        if (singleSelect) {
            if (onSingleChange) onSingleChange(trimmed);
            setQuery('');
            setIsOpen(false);
            return;
        }

        // Case-insensitive duplicate check
        const exists = value.some(t => t.toLowerCase() === trimmed.toLowerCase());
        if (!exists && onChange) {
            onChange([...value, trimmed]);
        }
        setQuery('');
    };

    const removeTag = (tagToRemove) => {
        if (singleSelect) {
            if (onSingleChange) onSingleChange('');
            return;
        }
        if (onChange) {
            onChange(value.filter(t => t !== tagToRemove));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            e.preventDefault();
            addTag(query);
        }
        if (e.key === 'Backspace' && !query && currentTags.length > 0) {
            removeTag(currentTags[currentTags.length - 1]);
        }
        if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    // Filter suggestions that aren't already selected
    const filteredSuggestions = suggestions.filter(
        s => !currentTags.some(t => t.toLowerCase() === s.tag.toLowerCase())
    );

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            {/* Tag chips + input */}
            <div
                className="flex flex-wrap items-center gap-1.5 min-h-[38px] px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-stone-900/20 focus-within:border-stone-500 cursor-text"
                onClick={() => { inputRef.current?.focus(); setIsOpen(true); }}
            >
                {currentTags.map(tag => {
                    const tagColor = suggestions.find(s => s.tag.toLowerCase() === tag.toLowerCase())?.color;
                    return (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-stone-100 text-stone-700 border border-stone-200"
                        style={tagColor ? { borderLeft: `3px solid ${tagColor}` } : {}}
                    >
                        <Tag className="w-3 h-3 text-stone-400" />
                        {tag}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                            className="hover:text-red-500 transition-colors"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                    );
                })}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={currentTags.length === 0 ? placeholder : ''}
                    className="flex-1 min-w-[80px] text-sm outline-none bg-transparent placeholder:text-stone-400"
                />
                <ChevronDown className="w-3.5 h-3.5 text-stone-300 shrink-0" />
            </div>

            {/* Autocomplete dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {loading ? (
                        <div className="px-3 py-2 text-xs text-stone-400">Loading tags...</div>
                    ) : filteredSuggestions.length > 0 ? (
                        <>
                            {filteredSuggestions.map(s => (
                                <button
                                    key={s.tag}
                                    type="button"
                                    onClick={() => addTag(s.tag)}
                                    className="flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-stone-50 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        {s.color ? (
                                            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                        ) : (
                                            <Tag className="w-3 h-3 text-stone-400" />
                                        )}
                                        <span className="text-stone-700">{s.tag}</span>
                                    </span>
                                    <span className="text-[10px] text-stone-400">{s.count} used</span>
                                </button>
                            ))}
                        </>
                    ) : query.trim() ? (
                        <button
                            type="button"
                            onClick={() => addTag(query)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-stone-50 transition-colors"
                        >
                            <Tag className="w-3 h-3 text-stone-400" />
                            <span className="text-stone-700">Create "{query.trim()}"</span>
                            <span className="text-[10px] text-stone-400 ml-auto">new tag</span>
                        </button>
                    ) : (
                        <div className="px-3 py-2 text-xs text-stone-400">Type to search or create tags</div>
                    )}
                </div>
            )}
        </div>
    );
}
